<template>
  <div class="px-5 pt-10 pb-6">
    <div class="max-w-1440 mx-auto overflow-hidden">
      <h2
        class="text-4xl text-primary-red mb-6"
        v-html="model.title"
      />
      <p class="text-xl mb-8 font-light">
        {{ model.description }}
      </p>

      <!-- Mobile Stories -->
      <div
        v-if="isMobile"
        class="some-stories relative"
      >
        <!-- Custom indicators -->
        <div class="flex gap-1 absolute top-0 mt-4 z-20 w-full px-3">
          <div
            v-for="(_, index) in totalSlides"
            :key="index"
            class="h-3 flex-1  overflow-hidden rounded-full relative"
          >
            <div
              class="some-slider-indicator h-full rounded-full"
              :class="{
                'active': activeIndex === index && started,
                'done': index < activeIndex,
                'paused': paused
              }"
              :style="{ '--slide-delay': delay + 'ms' }"
            >
              <span class="absolute top-0 left-0 bg-white w-full h-full rounded-full opacity-50" />
              <span class="bg-white h-full rounded-full relative" />
            </div>
          </div>
        </div>
        <div
          ref="swiperContainer"
          class="swiper"
        >
          <div class="swiper-wrapper">
            <div
              v-for="(card, index) in model.cards"
              :key="index"
              class="swiper-slide"
            >
              <so-me-card
                :card="card"
                @videoPlaying="updateDelay"
              />
            </div>
            <div
              v-for="(comments, slideIndex) in commentsSlides"
              :key="'comments-' + slideIndex"
              class="swiper-slide p-10 gap-3 flex flex-col px-4 bg-primary-red vertical"
            >
              <div
                v-for="(comment, cIndex) in comments"
                :key="cIndex"
                class="some-card-comment text-primary-red text-xs bg-white py-5 px-4 border-b"
              >
                <p
                  class="font-light mb-2"
                  :class="comment.rtlLayoutEnabled ? 'comment-rtl-alignment' : 'comment-ltr-alignment'"
                  v-html="comment.text"
                />
                <span class="font-bold">@{{ comment.username }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Hidden container for measuring comment heights -->
        <div
          ref="measureContainer"
          class="hidden-measurer"
        >
          <div
            v-for="(c, i) in commentCards"
            :key="'measure-' + i"
            class="some-card-comment text-primary-red text-xs bg-white py-5 px-4 border-b"
          >
            <p
              class="font-light mb-2"
              v-html="c.comment.text"
            />
            <span class="font-bold">@{{ c.comment.username }}</span>
          </div>
        </div>
      </div>

      <!-- Desktop Grid -->
      <div v-else class="grid grid-cols-3 gap-5 some-grid">
        <div v-for="(column, colIndex) in columns" :key="colIndex">
          <so-me-card
            v-for="(card, index) in column"
            :key="index"
            :card="card"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper'
import SoMeCard from '@/components/SoMeCard.vue'

export default {
  components: {
    SoMeCard
  },
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isMobile: window.matchMedia('(max-width: 639px)').matches,
      swiper: null,
      activeIndex: 0,
      previousIndex: 0,
      autoSliding: true,
      started: false,
      paused: false,
      autoSlideTimeout: null,
      autoSlideStart: 0,
      delay: 5000,
      defaultDelay: 5000,
      autoSlideRemaining: 5000,
      commentsSlides: []
    }
  },
  computed: {
    columns() {
      const col1 = []; const col2 = []; const
        col3 = []
      this.model.cards.forEach((item, index) => {
        if (index % 3 === 0) col1.push(item)
        else if (index % 3 === 1) col2.push(item)
        else col3.push(item)
      })
      return [col1, col2, col3]
    },
    totalSlides() {
      return this.model.cards.length + this.commentsSlides.length
    },
    commentCards() {
      return this.model.cards.filter((c) => c.comment)
    }
  },
  mounted() {
    if (this.isMobile) {
      this.splitCommentsIntoSlides()
      this.$nextTick(() => {
        setTimeout(() => {
          this.initSwiper()
        }, 20)
      })
    }
    window.addEventListener('resize', this.handleResize)

    this.$nextTick(() => {
      setTimeout(() => {
        this.started = true
      }, 10)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.destroySwiper()
  },
  methods: {
    initSwiper() {
      if (this.swiper) {
        return
      }

      this.swiper = new Swiper(this.$refs.swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 16
      })

      this.startAutoSlide()

      this.swiper.on('slideChange', () => {
        this.previousIndex = this.activeIndex
        this.activeIndex = this.swiper.activeIndex

        const direction = this.activeIndex > this.previousIndex ? 'next' : 'back'

        if (!this.autoSliding) {
          let someCardTitle = null
          const card = this.model.cards[this.activeIndex]
          if (card && card.content?.title) {
            someCardTitle = card.content.title
          }

          this.pushDataLayer({
            event: 'someGridScroll',
            someCardTitle,
            direction,
            placement: window.gtmData?.pageName
          })
        }

        this.autoSliding = true

        const prevSlide = this.swiper.slides[this.previousIndex]
        if (prevSlide) {
          const videos = prevSlide.querySelectorAll('video')
          videos.forEach((video) => {
            if (!video.paused) video.pause()
          })
        }

        if (this.activeIndex < this.previousIndex) {
          this.restartIndicator()
        }

        const isCommentSlide = this.activeIndex >= this.model.cards.length
        this.delay = isCommentSlide ? 20000 : this.defaultDelay

        this.autoSlideRemaining = this.delay
        this.startAutoSlide()
      })

      this.swiper.on('touchStart', () => {
        this.autoSliding = false
        this.paused = true
        clearTimeout(this.autoSlideTimeout)
        const elapsed = Date.now() - this.autoSlideStart
        this.autoSlideRemaining = Math.max(0, this.autoSlideRemaining - elapsed)
      })


      this.swiper.on('touchStart', () => {
        this.paused = true
        clearTimeout(this.autoSlideTimeout)
        const elapsed = Date.now() - this.autoSlideStart
        this.autoSlideRemaining = Math.max(0, this.autoSlideRemaining - elapsed)
      })

      this.swiper.on('touchEnd', () => {
        this.paused = false
        this.startAutoSlide(this.autoSlideRemaining)
      })
    },
    pushDataLayer(obj) {
      if (window.dataLayer) {
        window.dataLayer.push(obj)
      }
    },
    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true)
        this.swiper = null
      }
      clearTimeout(this.autoSlideTimeout)
    },
    handleResize() {
      const mobile = window.matchMedia('(max-width: 639px)').matches
      if (mobile && !this.swiper) {
        this.isMobile = true
        this.$nextTick(() => this.initSwiper())
      } else if (!mobile && this.swiper) {
        this.isMobile = false
        this.destroySwiper()
      }
    },
    startAutoSlide(overrideTime) {
      clearTimeout(this.autoSlideTimeout)
      if (!this.autoSliding || this.paused) {
        return
      }

      const time = overrideTime ?? this.delay
      this.autoSlideStart = Date.now()
      this.autoSlideRemaining = time

      this.autoSlideTimeout = setTimeout(() => {
        if (this.activeIndex < this.totalSlides - 1) {
          this.swiper.slideTo(this.activeIndex + 1, 0)
        } else {
          this.swiper.slideTo(0, 0)
        }

        this.autoSlideRemaining = this.delay
      }, time)
    },
    restartIndicator() {
      this.started = false
      this.$nextTick(() => {
        setTimeout(() => {
          this.started = true
        }, 10)
      })
    },
    updateDelay(videoDuration) {
      this.delay = videoDuration
      this.autoSlideRemaining = videoDuration

      clearTimeout(this.autoSlideTimeout)
      this.startAutoSlide(this.delay)
      this.restartIndicator()
    },
    splitCommentsIntoSlides() {
      const maxHeight = 555
      const comments = this.model.cards.filter((c) => c.comment).map((c) => c.comment)

      const slides = []
      let currentSlide = []
      let currentHeight = 0
      const measurers = this.$refs.measureContainer.children

      comments.forEach((comment, i) => {
        const element = measurers[i]
        const commentHeight = element.clientHeight + 10

        if (currentHeight + commentHeight > maxHeight && currentSlide.length > 0) {
          slides.push(currentSlide)
          currentSlide = [comment]
          currentHeight = commentHeight
        } else {
          currentSlide.push(comment)
          currentHeight += commentHeight
        }
      })

      if (currentSlide.length > 0) {
        slides.push(currentSlide)
      }

      this.commentsSlides = slides
    }
  }
}
</script>
